import moment from 'moment';
import { BufferedEventBus, IEventName } from '../events/BufferedEventBus';
import { Clock } from '../support/Clock';
import { DevSettings } from '../support/DevSettings';
import { RevenueCatModel } from './revenue-cat/RevenueCatModel';
import { IAsyncSmartStorageProvider, StorageProvider } from '../support/StorageProvider';
import { PaywallDialogViewModel } from './PaywallDialogViewModel';
import { Logger } from '../support/debug/Logger';

interface IPaywallNudgerState {
    nudges: number;             // how many nudges have we given the user?
    timerStart: string;         // time app first started OR last saw paywall OR last nudge
    pinDrops: number;           // how many pin drops/address searches since timerStart
    hasPlus: boolean;           // is user currently upgraded to AutoPylot+?
    plusStartedAt?: string;     // when we first detected they had plus
    plusEndedAt?: string;       // when we first detected they did not have plus
    recentViews: number[];      // array of timestamps of recent paywall views
}

export class PaywallNudgerStorage {
    private storage: IAsyncSmartStorageProvider<IPaywallNudgerState>;
    static inject = () => [StorageProvider];
    constructor(storageProvider: StorageProvider) {
        this.storage = storageProvider.getAsyncJSONProvider<IPaywallNudgerState>('paywall-nudger');
    }
    get = async () => this.storage.get();
    set = async (state: IPaywallNudgerState) => this.storage.set(state);
}

export class PaywallNudger {

    private state: IPaywallNudgerState = { hasPlus: false, nudges: 0, pinDrops: 0, timerStart: new Date().toISOString(), recentViews: [] };

    static inject = () => [
        BufferedEventBus,
        RevenueCatModel,
        PaywallDialogViewModel,
        DevSettings,
        Clock,
        Logger,
        PaywallNudgerStorage
    ];
    constructor(
        private events: BufferedEventBus,
        private revenueCat: RevenueCatModel,
        private paywallState: PaywallDialogViewModel,
        private devSettings: DevSettings,
        private clock: Clock,
        private log: Logger,
        private storage: PaywallNudgerStorage,
    ) {}

    start = async () => {
        try {
            // need to await here to be sure we have latest state before events start firing
            const storedState = await this.storage.get();

            if (storedState) {
                // use assign in case we've added additional attributes to state interface since this was stored
                Object.assign(this.state, storedState);
            }

            this.events.on('PaywallOpened', () => {
                this.handlePlusChange();
                this.updateState({ timerStart: new Date().toISOString(), recentViews: [Date.now(), ...this.state.recentViews.slice(0, 9)] });
            });

            this.events.on('AppStarted', () => {
                this.handlePlusChange();
                this.maybeNudge('AppStarted');
            });

            this.events.on('AppForegrounded', () => {
                this.handlePlusChange();
                this.maybeNudge('AppForegrounded');
            });

            this.events.on('AddressSelected', () => {
                this.handlePlusChange();
                this.updateState({ pinDrops: this.state.pinDrops + 1 });
                this.maybeNudge('AddressSelected');
            });

            this.events.on('PinDropped', () => {
                this.handlePlusChange();
                this.updateState({ pinDrops: this.state.pinDrops + 1 });
                this.maybeNudge('PinDropped');
            });

        } catch (e: any) {
            // don't prevent app starting
            e.message = `Failed to initialize PaywallNudger: ${e.message}`;
            this.log.error(e);
        }
    };

    private updateState = (updates: Partial<IPaywallNudgerState>) => {
        this.state = { ...this.state, ...updates };
        this.storage.set(this.state);
    };

    private getThresholds = (nudges: number) => {
        if (!nudges) {
            return { pinDrops: 2, days: 2 };
        }
        if (nudges === 1) {
            return { pinDrops: 8, days: 5 };
        }
        if (nudges === 2) {
            return { pinDrops: 20, days: 5 };
        }
        // 40, 60, etc
        return { pinDrops: (nudges - 1) * 20, days: 7 };
    };

    private handlePlusChange = () => {
        const hasPlus = this.revenueCat.hasUpgraded();

        if (!this.state.hasPlus && hasPlus) {
            // if user just upgraded, record start time
            this.updateState({ hasPlus, plusStartedAt: new Date().toISOString() });
        } else if (this.state.hasPlus && !hasPlus) {
            // if user just downgraded, record end time, and reset state to 2nd nudge
            this.updateState({ hasPlus, plusEndedAt: new Date().toISOString(), nudges: 1, pinDrops: 0, timerStart: new Date().toISOString(), recentViews: [] });
        }
    };

    private maybeNudge = (event: IEventName) => {
        const elapsedSeconds = moment(this.clock.now()).diff(this.state.timerStart, 'seconds');
        const dayInSeconds = (this.devSettings.rapidPaywallNudge ? 30 : 60*60*24);
        const elapsedDays = elapsedSeconds / dayInSeconds;
        const viewsToday = this.state.recentViews.filter(v => moment(this.clock.now()).startOf('d').isBefore(v)).length;

        // don't show if user has plus
        if (this.state.hasPlus) {
            return;
        }

        // during promotion, show twice a day on app open
        // (don't increment nudge count here to preserve current pin drop thresholds)
        if (this.paywallState.isPromotionActive() && (event === 'AppStarted' || event === 'AppForegrounded') && viewsToday < 2) {
            this.updateState({ timerStart: new Date().toISOString() });
            this.paywallState.open({ triggerSource: 'nudge' });
            return;
        }

        // if no promotion and eligible for trial, show on first app start ever
        // (don't increment nudge count here to preserve current pin drop thresholds)
        if (!this.paywallState.isPromotionActive() && this.revenueCat.isEligibleForTrial() && event === 'AppStarted' && !this.state.recentViews.length) {
            this.updateState({ timerStart: new Date().toISOString() });
            this.paywallState.open({ triggerSource: 'new-user' });
            return;
        }

        // if no promotion and eligible for trial, don't show if less than 24 hrs since first view
        if (!this.paywallState.isPromotionActive() && this.revenueCat.isEligibleForTrial() && this.state.nudges === 0 && elapsedDays < 1) {
            return;
        }

        // don't show if less than 2 days since last nudge (except during promotion)
        if (!this.paywallState.isPromotionActive() && this.state.nudges > 0 && elapsedDays < 2) {
            return;
        }

        // don't show if we haven't hit one of the thresholds
        const thresholds = this.getThresholds(this.state.nudges);
        if (this.state.pinDrops < thresholds.pinDrops && elapsedDays < thresholds.days) {
            return;
        }

        this.updateState({ nudges: this.state.nudges + 1, timerStart: new Date().toISOString() });
        this.paywallState.open({ triggerSource: 'nudge' });
    };
}
